import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateChannelUtmSourceMappingsRequest,
  NonIntegratedSourcingChannelsResponse,
  SourcingService,
} from 'api-clients/monolith';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { useValidationSchema } from './use-validation-schema';
import { FormInput, UtmMappingForm } from './utm-mapping-form';

interface UtmMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  manageChannelId?: string;
}

export function UtmMappingDialog({
  open,
  onOpenChange,
  manageChannelId,
}: UtmMappingDialogProps) {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const queryClient = useQueryClient();

  const existingChannelNames = useMemo(
    () =>
      queryClient
        .getQueryData<NonIntegratedSourcingChannelsResponse>([
          ...queryKeys.settings.channels.nonIntegrated,
        ])
        ?.sourcing_channels.map(channel => channel.channel_name) ?? [],
    [queryClient],
  );
  const validationSchema = useValidationSchema(existingChannelNames);

  const initialFormValues = useMemo<FormInput>(
    () => ({
      channelId: null,
      utmSourceIds: [],
      isNewChannel: false,
      channelName: null,
    }),
    [],
  );

  const hookForm = useForm<FormInput>({
    resolver: zodResolver(validationSchema),
    defaultValues: initialFormValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = hookForm;

  const { mutate: mapChannelUtmSources, isPending: isSubmitting } = useMutation(
    {
      mutationFn: (requestBody: CreateChannelUtmSourceMappingsRequest) =>
        SourcingService.postInternalApiSourcingSourcingChannels(requestBody),
      onSuccess: async () => {
        hookForm.reset(initialFormValues);
        onOpenChange(false);

        // Invalidate non-integrated channels query to update the UI
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.settings.channels.nonIntegrated],
        });

        // Invalidate UTM sources query to update the UI
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.settings.channels.utmMapping],
        });

        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Channel UTM sources mapped successfully',
            description: 'Success message for successful channel creation',
          }),
        });
      },
      onError: () => {
        notifyError({
          message: intl.formatMessage({
            defaultMessage: 'Failed to map channel UTM sources',
            description: 'Error message for failed channel UTM sources mapping',
          }),
        });
      },
    },
  );

  /* Submit the form data to the API */
  const handleMapChannelUtmSourcesSubmit = handleSubmit(() => {
    const formData = hookForm.getValues();
    const requestBody: CreateChannelUtmSourceMappingsRequest = {
      sourcing_channel: {
        channel_id: formData.channelId,
        channel_name: formData.channelName ?? null,
        utm_sources: formData.utmSourceIds,
      },
    };

    mapChannelUtmSources(requestBody);
  });

  /* Validate form and submit if valid */
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = await hookForm.trigger();
    if (!isValid) return;

    void handleMapChannelUtmSourcesSubmit();
  };

  /* Reset the form when the dialog is closed */
  const handleDialogOpenChange = (open: boolean) => {
    onOpenChange(open);

    if (!open) hookForm.reset(initialFormValues);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Configure channel mapping"
              description="Title of the dialog to map channels to UTM sources"
            />
          </DialogTitle>
        </DialogHeader>

        <form noValidate onSubmit={e => void handleFormSubmit(e)}>
          <div className="flex flex-col gap-6 py-4 px-6">
            {/* Title and description */}
            <div className="flex flex-col gap-1">
              <Typography variant="body2" as="h2">
                <FormattedMessage
                  defaultMessage="Add non-integrated channel"
                  description="Description of the form to add a non-integrated channel"
                />
              </Typography>
              <Typography variant="small1" className="!text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="Configure a new channel for tracking non-integrated sources"
                  description="Description of the form to map a non-integrated channel to UTM sources"
                />
              </Typography>
            </div>

            <UtmMappingForm
              hookForm={hookForm}
              manageChannelId={manageChannelId}
              onRequestSubmit={() => void handleMapChannelUtmSourcesSubmit()}
            />
          </div>

          {/* Save button - wrapped in form to submit the form */}
          <DialogFooter>
            <Button type="submit" disabled={!isDirty || isSubmitting}>
              {isSubmitting && <Loader2 className="animate-spin" />}
              <FormattedMessage
                defaultMessage="Save"
                description="Save button"
              />
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
